import { AppStoreImageValidationModal } from "@/components/AppStoreImageValidationModal";
import { FollowConfirmationModal } from "@/components/FollowConfirmationModal";
import { ClaimModal } from "@/components/QuestModals/ClaimModal";
import { ReferralInviteModal } from "@/components/QuestModals/ReferralInviteModal";
import { SocialEngagementModal } from "@/components/QuestModals/SocialEngagementModal";
import { TelegramQuestModal } from "@/components/QuestModals/TelegramQuestModal";
import { VideoQuestModal } from "@/components/QuestModals/VideoQuestModal";
import { CLAIM_MODAL_PARAMS, SOCIAL_LINK_ERROR_MESSAGES } from "@/constants";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { useAccessToken } from "@/hooks/useAccessToken";
import {
  linkedAccountPlatforms,
  openAuthorizeUrl,
  validateQuest,
} from "@/utils/linkedaccounts/linkedAccounts";
import type { SocialQuestData } from "@/utils/linkedaccounts/types";
import { trackLegacyEvent } from "@analytics/analytics";
import { Box } from "@biom3/react";
import type { Content } from "@prismicio/client";
import type { WishlistProps } from "@ui-kit/GameSpecs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LikeRetweetXModal } from "../../v2/LikeRetweetXModal";
import { useCampaign } from "../CampaignProvider";
import { XLikeAndRetweetModal } from "../X/XLikeAndRetweetModal";
import { XPostModal } from "../X/XPostModal";
import {
  type CampaignContent,
  type CampaignQuestState,
  mapCampaignQuestType,
} from "../types";
import { CampaignQuestList } from "./CampaignQuestList";

export type CampaignQuestsProps = {
  game: Content.GameDocument;
  wishlistProps: WishlistProps;
};

export const CampaignQuests = ({
  game,
  wishlistProps,
}: CampaignQuestsProps) => {
  const { xQuestDisabled } = useFlags();
  const {
    quests: campaignQuests,
    completeQuest,
    xQuestData,
    visibleCampaignContent,
  } = useCampaign();
  const getAccessToken = useAccessToken();
  const { trackV3 } = useAnalytics();
  const { isLoggedIn } = usePassportProvider();

  const campaignId = useMemo<number | undefined>(() => {
    const rawId = (visibleCampaignContent as CampaignContent)?.campaign
      ?.id as unknown;
    if (typeof rawId === "number")
      return Number.isFinite(rawId) ? rawId : undefined;
    if (rawId == null) return undefined;
    const numericId = Number(rawId);
    return Number.isFinite(numericId) ? numericId : undefined;
  }, [visibleCampaignContent]);

  // Modal states
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showXActionConfirmationModal, setShowXActionConfirmationModal] =
    useState(false);
  const [showLikeRetweetXModal, setShowLikeRetweetXModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inProgressQuest, setInProgressQuest] = useState<
    CampaignQuestState | undefined
  >(undefined);
  const [
    showAppStoreImageValidationModal,
    setShowAppStoreImageValidationModal,
  ] = useState(false);
  const [showReferralInvite, setShowReferralInvite] = useState(false);

  const questData: SocialQuestData = {
    ruleId: inProgressQuest?.externalId ?? "",
    key: inProgressQuest ? mapCampaignQuestType(inProgressQuest.type) : "bonus",
    questLink: inProgressQuest?.linkUrl ?? "",
    gameName: game.data.name || "",
    gameId: (game.data.games_service_item?.id as string) || "",
  };

  const onQuestClick = async (quest: CampaignQuestState) => {
    // Track quest click
    try {
      trackV3({
        object: "Quest",
        action: Action.PRESSED,
        extras: {
          questId: quest.externalId,
          questName: quest.name,
          gameId: quest.gameId,
          gameName: game.data.name ?? "",
          status: quest.completed ? "completed" : "incomplete",
          isLoggedIn: Boolean(isLoggedIn),
          section: "CampaignQuests",
          campaignId,
        },
      });
    } catch {
      // no-op analytics failure
    }
    switch (quest.type) {
      case "campaign_referrer":
        setInProgressQuest(quest);
        setShowReferralInvite(true);
        return;
      case "join_telegram_chat":
        if (quest.linkUrl) {
          setInProgressQuest(quest);
          window.open(quest.linkUrl, "_blank");
        }
        return;
      case "post_x_tweet":
      case "retweet_and_like":
        localStorage.removeItem("social_auth_error");
        localStorage.removeItem("social_quest_error");
        setInProgressQuest(quest);

        if (xQuestData[quest.externalId]?.data?.target_retweet_id) {
          setShowLikeRetweetXModal(true);
        } else {
          setShowXActionConfirmationModal(true);
        }
        return;
      case "follow_x_account":
      case "join_discord_server":
      case "steam_wishlist":
        if (linkedAccountPlatforms.get(mapCampaignQuestType(quest.type))) {
          localStorage.removeItem(quest.externalId);
          setShowConfirmation(true);
          if (quest.type === "follow_x_account" && xQuestDisabled) {
            setError(SOCIAL_LINK_ERROR_MESSAGES.X_FOLLOW_QUEST_UNAVAILABLE);
          } else {
            setInProgressQuest(quest);
            openAuthorizeUrl(
              quest.externalId,
              mapCampaignQuestType(quest.type),
              quest.linkUrl || "",
              game.data.name ?? "",
              (game.data.games_service_item?.id as string) ?? "",
              undefined, // loginOnly
              undefined, // tweetText
              undefined, // twitterHandle
              undefined, // tweetImageId
              quest.linkUrl, // linkUrl
            );
          }
        }
        return;
      case "app_store_image_validation":
        setShowAppStoreImageValidationModal(true);
        setInProgressQuest(quest);
        return;
      case "social_engagement":
      case "claim":
      case "trailer":
        setInProgressQuest(quest);
        return;
      case "no_validate":
      case "website":
        completeQuest(quest);
        return;
    }
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === "social_auth_error" &&
        event.newValue &&
        showConfirmation
      ) {
        setError(event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [showConfirmation]);

  const onConfirmFollow = useCallback(async () => {
    try {
      // If they failed to complete the quest make them complete the flow again so they can't spam the button and reach the rate limit
      if (error) {
        setShowConfirmation(false);
        setError(null);
        return;
      }

      const quest = inProgressQuest;
      if (!quest) {
        setShowConfirmation(false);
        setError(null);
        return;
      }

      // If the quest was completed in the login screen show the "success" state
      if (localStorage.getItem(quest.externalId) === "true") {
        setShowConfirmation(false);
        completeQuest(quest);
        setInProgressQuest(undefined);
        setError(null);
        return;
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }

      const imxGameId = (game.data.games_service_item?.id as string) ?? "";
      if (!imxGameId) {
        return;
      }

      const mappedKey = mapCampaignQuestType(quest.type);
      const following = await validateQuest(
        imxGameId as string,
        mappedKey,
        quest.externalId,
        accessToken,
        "",
      );
      if (!following) {
        throw new Error("the quest is not complete");
      }
      trackLegacyEvent({
        screen: "DetailsQuests",
        userJourney: "Games",
        action: "Succeeded",
        control: "SocialQuest",
        controlType: "Effect",
        extras: {
          questId: quest.externalId || "",
          platform: mappedKey,
          variant: "VariantA",
        },
      });

      setShowConfirmation(false);
      setError(null);
      completeQuest(quest);
      setInProgressQuest(undefined);
    } catch (err) {
      trackLegacyEvent({
        screen: "DetailsQuests",
        userJourney: "Games",
        control: "Failed",
        controlType: "Effect",
        extras: {
          questId: inProgressQuest?.externalId || "",
          platform: inProgressQuest
            ? mapCampaignQuestType(inProgressQuest.type)
            : "",
          variant: "VariantA",
        },
      });

      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while confirming follow.";

      setError(errorMessage);
    }
  }, [inProgressQuest, error, game, completeQuest, getAccessToken]);

  const claimModalTitle =
    inProgressQuest?.externalId &&
    CLAIM_MODAL_PARAMS[inProgressQuest?.externalId]
      ? CLAIM_MODAL_PARAMS[inProgressQuest?.externalId].title
      : (inProgressQuest?.name ?? "");
  const claimModalCaption =
    inProgressQuest?.externalId &&
    CLAIM_MODAL_PARAMS[inProgressQuest?.externalId]
      ? CLAIM_MODAL_PARAMS[inProgressQuest?.externalId].caption
      : undefined;

  return (
    <Box>
      <CampaignQuestList
        quests={campaignQuests}
        onQuestClick={onQuestClick}
        isWishlisted={wishlistProps.isWishlisted}
      />
      <ReferralInviteModal
        visible={showReferralInvite}
        onClose={() => {
          setShowReferralInvite(false);
          setInProgressQuest(undefined);
        }}
        gameUid={game.uid}
        campaignId={campaignId}
        campaignPoints={inProgressQuest?.campaignPoints}
      />
      <FollowConfirmationModal
        visible={showConfirmation}
        onConfirm={onConfirmFollow}
        onClose={() => {
          setShowConfirmation(false);
          setError(null);
          setInProgressQuest(undefined);
          localStorage.removeItem("social_auth_error");
        }}
        onRetry={() => {
          if (inProgressQuest) {
            setError(null);
            localStorage.removeItem("social_auth_error");
            window.open(inProgressQuest.linkUrl || "", "_blank");
          }
        }}
        onQuestClick={async () => {
          if (inProgressQuest) {
            await onQuestClick(inProgressQuest);
          }
        }}
        onSkip={() => {}}
        questKey={
          inProgressQuest
            ? mapCampaignQuestType(inProgressQuest.type)
            : undefined
        }
        linkUrl={inProgressQuest?.linkUrl}
        ruleId={inProgressQuest?.externalId}
        gameName={game?.data?.name || "the game"}
        error={error}
        setError={setError}
      />
      {inProgressQuest?.type === "post_x_tweet" &&
        xQuestData[inProgressQuest.externalId] && (
          <XPostModal
            visible={showXActionConfirmationModal}
            onClose={() => {
              setShowXActionConfirmationModal(false);
              setInProgressQuest(undefined);
            }}
            questData={questData}
            questContent={xQuestData[inProgressQuest.externalId]}
            questCompleted={inProgressQuest.completedToday}
            latestCompletion={inProgressQuest.latestCompletion}
            refreshPeriodHours={inProgressQuest.refreshPeriodHours ?? undefined}
            validFrom={inProgressQuest.validFrom ?? undefined}
          />
        )}
      {inProgressQuest?.type === "retweet_and_like" &&
        xQuestData[inProgressQuest.externalId] &&
        xQuestData[inProgressQuest.externalId]?.data?.target_retweet_id && (
          <LikeRetweetXModal
            visible={showLikeRetweetXModal}
            onClose={() => {
              setShowLikeRetweetXModal(false);
              setInProgressQuest(undefined);
            }}
            showConfirmationModal={() => {}}
            tweetId={
              xQuestData[inProgressQuest.externalId].data.target_retweet_id ||
              ""
            }
            gameId={questData.gameId}
            gameName={questData.gameName}
            questRuleId={questData.ruleId}
            questKey={"x-like-and-retweet-validation"}
            questLink={questData.questLink}
          />
        )}
      {/* to keep backwards compatible with old quests we can use old modal if no tweet ID is supplied */}
      {inProgressQuest?.type === "retweet_and_like" &&
        xQuestData[inProgressQuest.externalId] &&
        !xQuestData[inProgressQuest.externalId]?.data?.target_retweet_id && (
          <XLikeAndRetweetModal
            visible={showXActionConfirmationModal}
            onClose={() => {
              setShowXActionConfirmationModal(false);
              setInProgressQuest(undefined);
            }}
            questData={questData}
            questContent={xQuestData[inProgressQuest.externalId]}
          />
        )}
      {inProgressQuest?.type === "join_telegram_chat" && (
        <TelegramQuestModal
          gameId={questData.gameId}
          gameName={questData.gameName}
          questKey={"telegram-v2"}
          ruleId={inProgressQuest.externalId}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          onQuestComplete={() => {
            completeQuest(inProgressQuest);
          }}
        />
      )}
      {inProgressQuest?.type === "social_engagement" && (
        <SocialEngagementModal
          gameId={(game.data.games_service_item?.id as string) || ""}
          gameName={game.data.name || ""}
          visible={true}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          rewardType={"points"}
          hasMultiplier={inProgressQuest.categories.includes(
            "engagement_multiplier",
          )}
        />
      )}
      {inProgressQuest?.type === "claim" && (
        <ClaimModal
          title={claimModalTitle}
          caption={claimModalCaption}
          onViewQuest={() => {
            window.open(inProgressQuest.linkUrl || "", "_blank");
          }}
          onClaim={async () => {
            await completeQuest(inProgressQuest);
            setInProgressQuest(undefined);
          }}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
        />
      )}
      {inProgressQuest?.type === "app_store_image_validation" && (
        <AppStoreImageValidationModal
          visible={showAppStoreImageValidationModal}
          onClose={() => {
            setShowAppStoreImageValidationModal(false);
            setInProgressQuest(undefined);
          }}
          validationType={inProgressQuest?.validationType}
          questId={inProgressQuest?.externalId}
          gameUid={game?.uid || ""}
          gameId={(game.data.games_service_item?.id as string) || ""}
        />
      )}
      {inProgressQuest?.type === "trailer" && (
        <VideoQuestModal
          videoUrl={inProgressQuest.linkUrl || ""}
          videoMimeType={"iframe/embed"}
          onClose={() => {
            setInProgressQuest(undefined);
          }}
          onComplete={async () => {
            await completeQuest(inProgressQuest);
            setInProgressQuest(undefined);
          }}
        />
      )}
    </Box>
  );
};
